/**
 * Guards a reference id (`patientId`, `facilityId`) in a claim payload.
 *
 * Every such id is a REFERENCE, not a hint: the API contract says "if included, it will
 * lookup the <record> by id and ignore any other <record> fields". A stale id next to
 * edited fields therefore transmits the OLD record while the form shows the new one.
 *
 * The id is only safe to send while the form still describes the record it points at.
 * The baseline key comes from the CANONICAL record — the one picked in a lookup, or the
 * one fetched by id when a claim loads (`useVerifiedLink`) — never from the claim's own
 * fields, which would bless a stale id sitting beside edited fields. An id that cannot be
 * verified is not sent. `resolveLinkedId` drops the id once the current key differs.
 * Keys are computed from FORM-shaped values on both sides, so payload rewrites
 * (defaults, compacted address lines) cannot make a key drift from itself.
 */

/** Case and surrounding/internal whitespace are not identity: "oxnard" is "OXNARD". */
export function normalizeText(value: string | null | undefined): string {
    return (value ?? '').trim().replace(/\s+/g, ' ').toUpperCase();
}

/** The payload defaults a blank country to US, so a record's "" and a form's "US" are one address. */
export function normalizeCountry(country: string | null | undefined): string {
    return normalizeText(country) || 'US';
}

/** `dob` reaches the editor as `YYYY-MM-DD`, as the API's `[year, month, day]`, or as a `Date`. */
export function normalizeDob(dob: unknown): string {
    if (dob instanceof Date) {
        return Number.isNaN(dob.getTime()) ? '' : dob.toISOString().slice(0, 10);
    }
    if (Array.isArray(dob) && dob.length === 3) {
        const [year, month, day] = dob;
        return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    }
    return typeof dob === 'string' ? dob.trim() : '';
}

/** Joins with a separator no field contains, so a value shifted between fields differs. */
export function identityKey(parts: Array<string | null | undefined>): string {
    return parts.map(normalizeText).join('\u0000');
}

/** The id to send: `id` while `linkedKey` still equals `currentKey`, otherwise `undefined`. */
export function resolveLinkedId(
    id: string | null | undefined,
    linkedKey: string | null | undefined,
    currentKey: string,
): string | undefined {
    return id && linkedKey === currentKey ? id : undefined;
}

type Text = string | null | undefined;

/**
 * Every patient-record field the form edits. The API ignores ALL of them when
 * `patientId` is present, so an edit to any one of them must unlink the id.
 */
export interface PatientIdentityValues {
    nickname?: Text;
    first?: Text;
    middle?: Text;
    last?: Text;
    suffix?: Text;
    dob?: unknown;
    sex?: Text;
    externalId?: Text;
    phone?: Text;
    email?: Text;
    address1?: Text;
    address2?: Text;
    city?: Text;
    state?: Text;
    country?: Text;
    postal?: Text;
}

export function patientIdentityKey(v: PatientIdentityValues): string {
    return identityKey([
        v.nickname,
        v.first,
        v.middle,
        v.last,
        v.suffix,
        normalizeDob(v.dob),
        v.sex,
        v.externalId,
        v.phone,
        v.email,
        v.address1,
        v.address2,
        v.city,
        v.state,
        normalizeCountry(v.country),
        v.postal,
    ]);
}

/**
 * Every guarantor-record field the form edits. The API ignores ALL of them when
 * `guarantorId` is present, so an edit to any one of them must unlink the id.
 */
export interface GuarantorIdentityValues {
    first?: Text;
    middle?: Text;
    last?: Text;
    suffix?: Text;
    dob?: unknown;
    phone?: Text;
    email?: Text;
    organization?: Text;
    externalOrganizationId?: Text;
    address1?: Text;
    address2?: Text;
    city?: Text;
    state?: Text;
    country?: Text;
    postal?: Text;
}

export function guarantorIdentityKey(v: GuarantorIdentityValues): string {
    return identityKey([
        v.first,
        v.middle,
        v.last,
        v.suffix,
        normalizeDob(v.dob),
        v.phone,
        v.email,
        v.organization,
        v.externalOrganizationId,
        v.address1,
        v.address2,
        v.city,
        v.state,
        normalizeCountry(v.country),
        v.postal,
    ]);
}

/** Every facility-record field the form edits; shared by the claim and service-line forms. */
export interface FacilityIdentityValues {
    name?: Text;
    ein?: Text;
    npi?: Text;
    addressLine1?: Text;
    addressLine2?: Text;
    city?: Text;
    state?: Text;
    postalCode?: Text;
    country?: Text;
}

export function facilityIdentityKey(v: FacilityIdentityValues): string {
    return identityKey([
        v.name,
        v.ein,
        v.npi,
        v.addressLine1,
        v.addressLine2,
        v.city,
        v.state,
        v.postalCode,
        normalizeCountry(v.country),
    ]);
}
